import { XCircleIcon } from "@heroicons/react/24/outline";
import { GetCancelledOffer_getCancelledOffer } from "_graphql/visal/queries/__generated__/GetCancelledOffer";
import { Label, SectionTitle, Value } from "components/core";
import { FC } from "react";

type Props = {
  offer: GetCancelledOffer_getCancelledOffer | null | undefined;
};

const gridCols = "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-2";

const CancelledOfferView: FC<Props> = ({ offer }) => {
  const offerDetails =
    JSON.parse(offer?.offer_detail_with_deleted?.offer_details || "[]") || [];

  return (
    <div className="space-y-8">
      {/* Cancelled Alert */}
      <div className="rounded-md border border-red-200 bg-red-50 p-4">
        <div className="flex">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100">
            <XCircleIcon className="h-5 w-5 text-red-600" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-semibold text-red-900">
              Offer Cancelled
            </h3>
            <p className="mt-1 text-sm text-red-800">
              {offer?.offer_cancel_reason || "No reason provided"}
            </p>
          </div>
        </div>
      </div>

      {/* Offer Information */}
      <div>
        <SectionTitle>Offer Information</SectionTitle>
        <div className={gridCols}>
          <div className="lg:col-span-2">
            <Label>Cedant Name</Label>
            <Value>{offer?.insurer?.insurer_company_name}</Value>
          </div>

          <div>
            <Label>Business Name</Label>
            <Value>{offer?.classofbusiness?.business_name}</Value>
          </div>
        </div>
      </div>

      {/* Business Class Details */}
      <div className="pt-6 border-t border-gray-100">
        <SectionTitle>Business Class Details</SectionTitle>
        <div className={gridCols}>
          {offerDetails.map((detail: any, idx: number) => (
            <div
              key={idx}
              className="rounded-md border border-gray-100 bg-gray-50 p-3"
            >
              <Label>{detail.keydetail}</Label>
              <Value>{detail?.value}</Value>
            </div>
          ))}
        </div>
      </div>

      {/* Offer Details */}
      <div className="pt-6 border-t border-gray-100">
        <SectionTitle>Offer Details</SectionTitle>
        <div className={gridCols}>
          <div>
            <Label>Policy Number</Label>
            <Value>{offer?.offer_detail_with_deleted?.policy_number}</Value>
          </div>

          <div>
            <Label>Insured</Label>
            <Value>{offer?.offer_detail_with_deleted?.insured_by}</Value>
          </div>

          <div>
            <Label>Rate</Label>
            <Value>{offer?.rate}</Value>
          </div>

          <div>
            <Label>Premium</Label>
            <Value>{offer?.premium}</Value>
          </div>

          <div>
            <Label>Facultative Offer</Label>
            <Value>
              {offer?.facultative_offer != null
                ? `${offer.facultative_offer} %`
                : null}
            </Value>
          </div>

          <div>
            <Label>Commission</Label>
            <Value>
              {offer?.commission != null ? `${offer.commission} %` : null}
            </Value>
          </div>

          <div>
            <Label>Brokerage</Label>
            <Value>
              {offer?.brokerage != null ? `${offer.brokerage} %` : null}
            </Value>
          </div>

          <div>
            <Label>Currency</Label>
            <Value>{offer?.offer_detail_with_deleted?.currency}</Value>
          </div>
        </div>
      </div>

      {/* Period of Insurance */}
      <div className="pt-6 border-t border-gray-100">
        <SectionTitle>Period of Insurance</SectionTitle>
        <div className={gridCols}>
          <div>
            <Label>From</Label>
            <Value>
              {offer?.offer_detail_with_deleted?.period_of_insurance_from}
            </Value>
          </div>

          <div>
            <Label>To</Label>
            <Value>
              {offer?.offer_detail_with_deleted?.period_of_insurance_to}
            </Value>
          </div>
        </div>
      </div>

      {/* Comment */}
      <div className="pt-6 border-t border-gray-100">
        <SectionTitle>Comment</SectionTitle>
        <div className="mt-2">
          <div
            className="prose prose-sm max-w-none text-gray-700"
            dangerouslySetInnerHTML={{
              __html:
                offer?.offer_detail_with_deleted?.offer_comment ||
                "<span class='italic text-gray-400'>—</span>",
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default CancelledOfferView;
